'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, RotateCcw, SlidersHorizontal, Search, X } from 'lucide-react';

interface FilterSidebarProps {
  onFiltersChange?: (filters: FilterState) => void;
}

export interface FilterState {
  streams: string[];
  states: string[];
  cities: string[];
  ownership: string[];
  accreditation: string[];
  feesMin: number;
  feesMax: number;
  nirfRankMax: number;
  hostel: boolean;
  scholarships: boolean;
}

const streams = [
  { label: 'Engineering', icon: '⚙️' },
  { label: 'Medical', icon: '🏥' },
  { label: 'Management', icon: '📊' },
  { label: 'Law', icon: '⚖️' },
  { label: 'Arts', icon: '🎨' },
  { label: 'Commerce', icon: '💼' },
  { label: 'Science', icon: '🔬' },
  { label: 'Design', icon: '✏️' },
];

const states = ['Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Gujarat', 'Rajasthan', 'West Bengal', 'Punjab', 'Uttar Pradesh', 'Telangana', 'Kerala', 'Madhya Pradesh'];
const cities = ['New Delhi', 'Mumbai', 'Ahmedabad', 'Bangalore', 'Chennai', 'Vellore', 'Mohali', 'Manipal', 'Pilani', 'Tiruchirappalli', 'Kolkata', 'Hyderabad', 'Pune', 'Jaipur'];
const ownerships = [
  { label: 'Government', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { label: 'Private', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { label: 'Deemed', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { label: 'Autonomous', color: 'bg-teal-100 text-teal-700 border-teal-200' },
];
const accreditations = [
  { label: 'NAAC A++', color: 'bg-emerald-500', stars: 5 },
  { label: 'NAAC A+', color: 'bg-green-500', stars: 4 },
  { label: 'NAAC A', color: 'bg-lime-500', stars: 3 },
  { label: 'NAAC B++', color: 'bg-yellow-500', stars: 2 },
  { label: 'NBA', color: 'bg-blue-500', stars: 3 },
];

function FilterGroup({ title, children, defaultOpen = true, count = 0 }: { title: string; children: React.ReactNode; defaultOpen?: boolean; count?: number }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-left px-4 py-3"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-foreground">{title}</span>
          {count > 0 && (
            <span className="bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
              {count}
            </span>
          )}
        </div>
        {open ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

export default function FilterSidebar({ onFiltersChange }: FilterSidebarProps) {
  const [selectedStreams, setSelectedStreams] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedOwnership, setSelectedOwnership] = useState<string[]>([]);
  const [selectedAccreditation, setSelectedAccreditation] = useState<string[]>([]);
  const [feesMax, setFeesMax] = useState(5000000);
  const [nirfRankMax, setNirfRankMax] = useState(200);
  const [hostel, setHostel] = useState(false);
  const [scholarships, setScholarships] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  // Use refs to always have latest values for notify
  const stateRef = useRef({ selectedStreams, selectedStates, selectedCities, selectedOwnership, selectedAccreditation, feesMax, nirfRankMax, hostel, scholarships });
  useEffect(() => {
    stateRef.current = { selectedStreams, selectedStates, selectedCities, selectedOwnership, selectedAccreditation, feesMax, nirfRankMax, hostel, scholarships };
  });

  const notify = (patch: Partial<FilterState>) => {
    if (!onFiltersChange) return;
    const s = stateRef.current;
    onFiltersChange({
      streams: s.selectedStreams,
      states: s.selectedStates,
      cities: s.selectedCities,
      ownership: s.selectedOwnership,
      accreditation: s.selectedAccreditation,
      feesMin: 0,
      feesMax: s.feesMax,
      nirfRankMax: s.nirfRankMax,
      hostel: s.hostel,
      scholarships: s.scholarships,
      ...patch,
    });
  };

  const toggleStream = (item: string) => {
    const next = selectedStreams.includes(item) ? selectedStreams.filter(x => x !== item) : [...selectedStreams, item];
    setSelectedStreams(next);
    notify({ streams: next });
  };

  const toggleState = (item: string) => {
    const next = selectedStates.includes(item) ? selectedStates.filter(x => x !== item) : [...selectedStates, item];
    setSelectedStates(next);
    notify({ states: next });
  };

  const toggleCity = (item: string) => {
    const next = selectedCities.includes(item) ? selectedCities.filter(x => x !== item) : [...selectedCities, item];
    setSelectedCities(next);
    notify({ cities: next });
  };

  const toggleOwnership = (item: string) => {
    const next = selectedOwnership.includes(item) ? selectedOwnership.filter(x => x !== item) : [...selectedOwnership, item];
    setSelectedOwnership(next);
    notify({ ownership: next });
  };

  const toggleAccreditation = (item: string) => {
    const next = selectedAccreditation.includes(item) ? selectedAccreditation.filter(x => x !== item) : [...selectedAccreditation, item];
    setSelectedAccreditation(next);
    notify({ accreditation: next });
  };

  const handleFeesMax = (val: number) => { setFeesMax(val); notify({ feesMax: val }); };
  const handleNirfRankMax = (val: number) => { setNirfRankMax(val); notify({ nirfRankMax: val }); };
  const handleHostel = (val: boolean) => { setHostel(val); notify({ hostel: val }); };
  const handleScholarships = (val: boolean) => { setScholarships(val); notify({ scholarships: val }); };

  const resetAll = () => {
    setSelectedStreams([]); setSelectedStates([]); setSelectedCities([]); setSelectedOwnership([]); setSelectedAccreditation([]);
    setFeesMax(5000000); setNirfRankMax(200); setHostel(false); setScholarships(false); setStateSearch(''); setCitySearch('');
    if (onFiltersChange) onFiltersChange({ streams: [], states: [], cities: [], ownership: [], accreditation: [], feesMin: 0, feesMax: 5000000, nirfRankMax: 200, hostel: false, scholarships: false });
  };

  const totalActive = selectedStreams.length + selectedStates.length + selectedCities.length + selectedOwnership.length + selectedAccreditation.length + (hostel ? 1 : 0) + (scholarships ? 1 : 0);
  const filteredStates = states.filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()));
  const filteredCities = cities.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));

  const feeLabel = (val: number) => val >= 5000000 ? 'Any' : val >= 100000 ? `₹${(val / 100000).toFixed(0)}L` : `₹${(val / 1000).toFixed(0)}K`;

  const allActiveTags = [
    ...selectedStreams.map(t => ({ tag: t, type: 'stream' })),
    ...selectedStates.map(t => ({ tag: t, type: 'state' })),
    ...selectedCities.map(t => ({ tag: t, type: 'city' })),
    ...selectedOwnership.map(t => ({ tag: t, type: 'ownership' })),
    ...selectedAccreditation.map(t => ({ tag: t, type: 'accreditation' })),
  ];

  return (
    <div className="bg-card border border-border rounded-2xl sticky top-24 overflow-y-auto scrollbar-thin" style={{ maxHeight: 'calc(100vh - 7rem)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} className="text-primary" />
          <span className="font-bold text-sm text-foreground">Filters</span>
          {totalActive > 0 && (
            <span className="bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
              {totalActive}
            </span>
          )}
        </div>
        {totalActive > 0 && (
          <button onClick={resetAll} className="flex items-center gap-1 text-xs text-primary font-semibold hover:opacity-80 transition-opacity">
            <RotateCcw size={11} /> Reset All
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {totalActive > 0 && (
        <div className="px-4 py-2.5 border-b border-border flex flex-wrap gap-1.5">
          {allActiveTags.map(({ tag, type }) => (
            <span key={`chip-${type}-${tag}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded-full">
              {tag}
              <button onClick={() => {
                if (type === 'stream') toggleStream(tag);
                else if (type === 'state') toggleState(tag);
                else if (type === 'city') toggleCity(tag);
                else if (type === 'ownership') toggleOwnership(tag);
                else if (type === 'accreditation') toggleAccreditation(tag);
              }}><X size={10} /></button>
            </span>
          ))}
          {hostel && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded-full">
              Hostel <button onClick={() => handleHostel(false)}><X size={10} /></button>
            </span>
          )}
          {scholarships && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded-full">
              Scholarships <button onClick={() => handleScholarships(false)}><X size={10} /></button>
            </span>
          )}
        </div>
      )}

      {/* Stream / Course Type */}
      <FilterGroup title="Stream / Course" defaultOpen={true} count={selectedStreams.length}>
        <div className="flex flex-wrap gap-2">
          {streams.map(({ label, icon }) => {
            const active = selectedStreams.includes(label);
            return (
              <button
                key={`stream-${label}`}
                onClick={() => toggleStream(label)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                  active ? 'bg-primary text-white border-primary shadow-sm' : 'bg-muted/50 text-foreground border-border hover:border-primary hover:text-primary'
                }`}
              >
                <span>{icon}</span>{label}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      {/* City Selection */}
      <FilterGroup title="City" defaultOpen={true} count={selectedCities.length}>
        <div className="relative mb-2.5">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search city..."
            value={citySearch}
            onChange={e => setCitySearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border bg-muted/30 focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="space-y-1.5 max-h-44 overflow-y-auto scrollbar-thin pr-1">
          {filteredCities.map(city => {
            const active = selectedCities.includes(city);
            return (
              <label key={`city-${city}`} className="flex items-center gap-2.5 cursor-pointer group py-0.5" onClick={() => toggleCity(city)}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${active ? 'bg-primary border-primary' : 'border-border group-hover:border-primary'}`}>
                  {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <span className={`text-sm transition-colors ${active ? 'text-primary font-semibold' : 'text-foreground group-hover:text-primary'}`}>{city}</span>
              </label>
            );
          })}
          {filteredCities.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">No cities found</p>}
        </div>
      </FilterGroup>

      {/* Location / State with search */}
      <FilterGroup title="State" defaultOpen={false} count={selectedStates.length}>
        <div className="relative mb-2.5">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search state..."
            value={stateSearch}
            onChange={e => setStateSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border bg-muted/30 focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="space-y-1.5 max-h-44 overflow-y-auto scrollbar-thin pr-1">
          {filteredStates.map(state => {
            const active = selectedStates.includes(state);
            return (
              <label key={`state-${state}`} className="flex items-center gap-2.5 cursor-pointer group py-0.5" onClick={() => toggleState(state)}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${active ? 'bg-primary border-primary' : 'border-border group-hover:border-primary'}`}>
                  {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <span className={`text-sm transition-colors ${active ? 'text-primary font-semibold' : 'text-foreground group-hover:text-primary'}`}>{state}</span>
              </label>
            );
          })}
          {filteredStates.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">No states found</p>}
        </div>
      </FilterGroup>

      {/* Total Fees */}
      <FilterGroup title="Total Fees (₹)" defaultOpen={true}>
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">₹0</span>
            <span className="text-sm font-bold text-primary">{feeLabel(feesMax)}</span>
            <span className="text-xs text-muted-foreground">₹50L+</span>
          </div>
          <div className="relative h-5 flex items-center">
            <div className="absolute left-0 right-0 h-1.5 bg-muted rounded-full" />
            <div className="absolute left-0 h-1.5 bg-primary rounded-full" style={{ width: `${(feesMax / 5000000) * 100}%` }} />
            <input
              type="range" min={0} max={5000000} step={100000} value={feesMax}
              onChange={e => handleFeesMax(Number(e.target.value))}
              className="absolute w-full opacity-0 cursor-pointer h-5 z-10"
            />
            <div className="absolute w-4 h-4 bg-primary rounded-full border-2 border-white shadow-md pointer-events-none" style={{ left: `calc(${(feesMax / 5000000) * 100}% - 8px)` }} />
          </div>
          <div className="grid grid-cols-4 gap-1.5 mt-1">
            {[500000, 1000000, 2000000, 5000000].map(val => (
              <button key={`fees-${val}`} onClick={() => handleFeesMax(val)}
                className={`text-xs py-1.5 px-1 rounded-lg border font-semibold transition-all ${feesMax === val ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-primary'}`}>
                {val === 500000 ? '<5L' : val === 1000000 ? '<10L' : val === 2000000 ? '<20L' : 'Any'}
              </button>
            ))}
          </div>
        </div>
      </FilterGroup>

      {/* NIRF Rank */}
      <FilterGroup title="NIRF Rank" defaultOpen={true}>
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Top 1</span>
            <span className="text-sm font-bold text-primary">Top {nirfRankMax >= 200 ? '200+' : nirfRankMax}</span>
            <span className="text-xs text-muted-foreground">200+</span>
          </div>
          <div className="relative h-5 flex items-center">
            <div className="absolute left-0 right-0 h-1.5 bg-muted rounded-full" />
            <div className="absolute left-0 h-1.5 bg-primary rounded-full" style={{ width: `${(nirfRankMax / 200) * 100}%` }} />
            <input
              type="range" min={1} max={200} step={5} value={nirfRankMax}
              onChange={e => handleNirfRankMax(Number(e.target.value))}
              className="absolute w-full opacity-0 cursor-pointer h-5 z-10"
            />
            <div className="absolute w-4 h-4 bg-primary rounded-full border-2 border-white shadow-md pointer-events-none" style={{ left: `calc(${(nirfRankMax / 200) * 100}% - 8px)` }} />
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {[10, 25, 50, 100, 150, 200].map(rank => (
              <button key={`rank-${rank}`} onClick={() => handleNirfRankMax(rank)}
                className={`text-xs py-1.5 rounded-lg border font-semibold transition-all ${nirfRankMax === rank ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-primary'}`}>
                Top {rank}
              </button>
            ))}
          </div>
        </div>
      </FilterGroup>

      {/* Ownership Type */}
      <FilterGroup title="Ownership Type" defaultOpen={false} count={selectedOwnership.length}>
        <div className="flex flex-wrap gap-2">
          {ownerships.map(({ label, color }) => {
            const active = selectedOwnership.includes(label);
            return (
              <button key={`own-${label}`}
                onClick={() => toggleOwnership(label)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${active ? 'bg-primary text-white border-primary' : `${color} hover:opacity-80`}`}>
                {label}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      {/* Accreditation */}
      <FilterGroup title="Accreditation" defaultOpen={true} count={selectedAccreditation.length}>
        <div className="space-y-2">
          {accreditations.map(({ label, color }) => {
            const active = selectedAccreditation.includes(label);
            return (
              <div key={`acc-${label}`} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => toggleAccreditation(label)}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${active ? 'bg-primary border-primary' : 'border-border group-hover:border-primary'}`}>
                  {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${color} shrink-0`} />
                  <span className={`text-sm font-semibold transition-colors ${active ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>{label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </FilterGroup>

      {/* Amenities */}
      <FilterGroup title="Amenities" defaultOpen={true} count={(hostel ? 1 : 0) + (scholarships ? 1 : 0)}>
        <div className="space-y-3">
          {[
            { label: 'Hostel Available', icon: '🏠', value: hostel, onChange: handleHostel },
            { label: 'Scholarships Available', icon: '🎓', value: scholarships, onChange: handleScholarships },
          ].map(({ label, icon, value, onChange }) => (
            <div key={label} className="flex items-center justify-between cursor-pointer group" onClick={() => onChange(!value)}>
              <div className="flex items-center gap-2">
                <span className="text-base">{icon}</span>
                <span className={`text-sm font-semibold transition-colors ${value ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>{label}</span>
              </div>
              <div
                className={`relative w-10 h-5 rounded-full transition-all cursor-pointer ${value ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${value ? 'left-5' : 'left-0.5'}`} />
              </div>
            </div>
          ))}
        </div>
      </FilterGroup>
    </div>
  );
}